
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchAdminPremiumBots, 
  fetchAdminPremiumBotById, 
  createPremiumBot,
  updatePremiumBot,
  deletePremiumBot
} from '@/services/admin/admin-premium-bot-service';
import { useState } from 'react';
import { PremiumBot } from '@/types/premium-bots';
import { toast } from 'sonner';

export const useAdminPremiumBots = () => {
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    searchQuery: '',
    sortBy: 'created_date',
    sortOrder: 'desc' as 'asc' | 'desc',
    status: 'all',
  });

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminPremiumBots', searchParams],
    queryFn: () => fetchAdminPremiumBots(searchParams),
  });

  const setPage = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const setPageSize = (pageSize: number) => {
    setSearchParams(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const setSearchQuery = (searchQuery: string) => {
    setSearchParams(prev => ({ ...prev, searchQuery, page: 1 }));
  };

  const setSortBy = (sortBy: string) => {
    setSearchParams(prev => ({ ...prev, sortBy }));
  };

  const setSortOrder = (sortOrder: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sortOrder }));
  };

  const setStatus = (status: string) => {
    setSearchParams(prev => ({ ...prev, status, page: 1 }));
  };

  return {
    premiumBots: data?.bots ?? [],
    totalCount: data?.totalCount ?? 0,
    currentPage: data?.currentPage ?? 1,
    pageSize: data?.pageSize ?? 10,
    totalPages: data?.totalPages ?? 1,
    isLoading,
    isError,
    error,
    refetch,
    searchParams,
    setPage,
    setPageSize,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setStatus,
  };
};

export const useAdminPremiumBotDetail = (botId: string | undefined) => {
  return useQuery({
    queryKey: ['adminPremiumBot', botId],
    queryFn: () => fetchAdminPremiumBotById(botId!),
    enabled: !!botId,
  });
};

export const useCreatePremiumBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (botData: Partial<PremiumBot>) => createPremiumBot(botData),
    onSuccess: () => {
      toast.success('Premium bot created successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPremiumBots'] });
    },
    onError: (error: any) => {
      toast.error(`Error creating premium bot: ${error.message}`);
    }
  });
};

export const useUpdatePremiumBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ botId, botData }: { botId: string; botData: Partial<PremiumBot> }) => 
      updatePremiumBot(botId, botData),
    onSuccess: (_, { botId }) => {
      toast.success('Premium bot updated successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPremiumBots'] });
      queryClient.invalidateQueries({ queryKey: ['adminPremiumBot', botId] });
    },
    onError: (error: any) => {
      toast.error(`Error updating premium bot: ${error.message}`);
    }
  });
};

export const useDeletePremiumBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (botId: string) => deletePremiumBot(botId),
    onSuccess: () => {
      toast.success('Premium bot deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPremiumBots'] });
    },
    onError: (error: any) => {
      toast.error(`Error deleting premium bot: ${error.message}`);
    }
  });
};
